import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";

interface IUseBarcodeFieldOptions {
  name: string;
  onScanned?: (value: string) => void;
}

interface IUseBarcodeFieldResult {
  isScannerOpen: boolean;
  openScanner: () => void;
  closeScanner: () => void;
  handleKeyDown: (
    event: React.KeyboardEvent<HTMLInputElement>,
    currentValue: string | null | undefined
  ) => void;
  handleScannerResult: (
    value: string,
    onChange: (value: string) => void
  ) => void;
}

/**
 * All behavioral logic for `BarcodeField` — opening the scanner modal,
 * reacting to Enter (for USB scanners + manual typing), and routing a
 * successful scan back into the react-hook-form field.
 *
 * The component file should contain only JSX wiring that reads this hook.
 */
export const useBarcodeField = ({
  name,
  onScanned,
}: IUseBarcodeFieldOptions): IUseBarcodeFieldResult => {
  const { setValue } = useFormContext();
  const [isScannerOpen, setIsScannerOpen] = useState(false);

  const openScanner = useCallback(() => setIsScannerOpen(true), []);
  const closeScanner = useCallback(() => setIsScannerOpen(false), []);

  const handleKeyDown = useCallback(
    (
      event: React.KeyboardEvent<HTMLInputElement>,
      currentValue: string | null | undefined
    ) => {
      if (event.key !== "Enter") return;
      const trimmed = (currentValue ?? "").toString().trim();
      if (!trimmed) return;

      // USB scanners type then press Enter; we don't want to submit the
      // enclosing form on that Enter — the scan itself is the "action".
      event.preventDefault();
      onScanned?.(trimmed);
    },
    [onScanned]
  );

  const handleScannerResult = useCallback(
    (value: string, onChange: (value: string) => void) => {
      onChange(value);
      setValue(name, value, { shouldDirty: true, shouldValidate: true });
      onScanned?.(value);
      setIsScannerOpen(false);
    },
    [name, onScanned, setValue]
  );

  return {
    isScannerOpen,
    openScanner,
    closeScanner,
    handleKeyDown,
    handleScannerResult,
  };
};
